import type { Helpers } from "./endpoints-shared";
import type { Download } from "./client";
import type { In, Out } from "./operations";

export function posOperationsEndpoints({
  http,
  get,
  post,
  put,
  patch,
  del,
}: Pick<Helpers, "http" | "get" | "post" | "put" | "patch" | "del">) {
  return {
    createExitRequest: (input: In<"pos_create_exit_request">) => post<Out<"pos_create_exit_request">>("/pos/exit-requests", input),
    exitRequestStatus: (id: string) => get<Out<"pos_exit_request_status">>(`/pos/exit-requests/${id}`),
    cancelExitRequest: (id: string) => del<void>(`/pos/exit-requests/${id}`),
    consumeExitRequest: (id: string) => post<Out<"pos_consume_exit_request">>(`/pos/exit-requests/${id}/consume`),
    inspectExitRequest: (id: string, input: In<"pos_inspect_exit_request">, club: string) => post<Out<"pos_inspect_exit_request">>(`/pos/exit-requests/${id}/inspect`, input, { club }),
    decideExitRequest: (id: string, input: In<"pos_decide_exit_request">, club: string) => post<Out<"pos_decide_exit_request">>(`/pos/exit-requests/${id}/decision`, input, { club }),
    previewRouting: (input: In<"pos_preview_routing">) => post<Out<"pos_preview_routing">>("/pos/operations/routing-preview", input),
    listAgentInstallers: (signal?: AbortSignal) => get<Out<"pos_installers">>("/pos/operations/agent-installers", { signal }),
    createAgentSetup: (input: In<"pos_create_setup">) => post<Out<"pos_create_setup">>("/pos/operations/agent-setup", input),
    downloadAgentInstaller: (platform: "windows" | "linux", signal?: AbortSignal) =>
      http.request<Download>("GET", `/pos/operations/agent-installers/${platform}`, { responseType: "blob", signal }),
    listStations: (outletId?: string, signal?: AbortSignal) =>
      get<Out<"pos_stations">>("/pos/operations/stations", {
        query: { outlet_id: outletId },
        signal,
      }),
    createStation: (input: In<"pos_add_station">) =>
      post<Out<"pos_add_station">>("/pos/operations/stations", input),
    updateStation: (id: string, input: In<"pos_update_station">) =>
      put<Out<"pos_update_station">>(`/pos/operations/stations/${id}`, input),
    listKitchenTickets: (stationId: string, signal?: AbortSignal) =>
      get<Out<"pos_tickets">>("/pos/operations/tickets", {
        query: { station_id: stationId },
        signal,
      }),
    updateKitchenTicket: (id: string, input: In<"pos_update_ticket">) =>
      patch<Out<"pos_update_ticket">>(`/pos/operations/tickets/${id}`, input),
    listPrinters: (signal?: AbortSignal) =>
      get<Out<"pos_printers">>("/pos/operations/printers", { signal }),
    updatePrinter: (id: string, input: In<"pos_update_printer">) =>
      patch<Out<"pos_update_printer">>(`/pos/operations/printers/${id}`, input),
    listAgents: (signal?: AbortSignal) =>
      get<Out<"pos_agents">>("/pos/operations/agents", { signal }),
    createAgent: (input: In<"pos_add_agent">) =>
      post<Out<"pos_add_agent">>("/pos/operations/agents", input),
    revokeAgent: (id: string) => del<void>(`/pos/operations/agents/${id}`),
    listTerminals: (signal?: AbortSignal) =>
      get<Out<"pos_terminals">>("/pos/operations/terminals", { signal }),
    reportTerminal: (input: In<"pos_browser_heartbeat">) =>
      post<Out<"pos_browser_heartbeat">>(
        "/pos/operations/terminals/heartbeat",
        input,
      ),
    listPrintJobs: (signal?: AbortSignal) =>
      get<Out<"pos_jobs">>("/pos/operations/print-jobs", { signal }),
    retryPrintJob: (id: string) =>
      post<Out<"pos_retry_job">>(`/pos/operations/print-jobs/${id}/retry`),
    resolvePrintJob: (id: string, input: In<"pos_resolve_job">) =>
      post<Out<"pos_resolve_job">>(
        `/pos/operations/print-jobs/${id}/resolve`,
        input,
      ),
    getOutletPrinting: (outletId: string, signal?: AbortSignal) =>
      get<Out<"pos_printing">>(`/pos/operations/outlets/${outletId}/printing`, {
        signal,
      }),
    updateOutletPrinting: (
      outletId: string,
      input: In<"pos_update_printing">,
    ) =>
      put<Out<"pos_update_printing">>(
        `/pos/operations/outlets/${outletId}/printing`,
        input,
      ),
  };
}
